import { useEffect, useState } from "react";
import { Occurrence, OccurrenceDispatch } from "../models/occurrence";
import useDealership from "./useDealership";
import { Dealership } from "../models/dealership";
import { fetchDealershipsByGeolocation, fetchDealerships, fetchDealershipsByStateAndCity, fetchDealershipsByDN } from "../api/fetchDealerships";
import { getAddressByGeoLocation } from "../services/assetService";
import { createv2Route, getRoute, Route } from "../api/routing";
import { useDrivers } from "./useDrivers";
import { AssetGeoLocation } from "../models/asset";
import { fetchAssetGeoLocation } from "../api/fetchAssets";
import { updateOccurrence } from "../api/occurrence";
import { useIntl } from "react-intl";
import { getDistanceBetweenTwoLocations } from "../utils/locationUtils";
import useDarkMode from "./useDarkMode";

export const reasonsRefusalOptions = [
    { id: 'CUSTOMER_CANCELLED', label: 'Cliente cancelou o atendimento'},
    { id: 'DN_DID_NOT_ANSWER', label: 'Falha de comunicação (DN não atendeu)'},
    { id: 'DN_PHONE_NOT_ANSWERED', label: 'Não atendeu o telefone'},
    { id: "NO_RESOURCE", label: "Não possui recursos" },
    { id: "NO_PARTS", label: "Não possui peças" },
    { id: "NO_STAFF", label: "Não possui técnico" },
    { id: "NA", label: "Outro" },
];

export const optionsOccurrenceType = [
    { id: "ASSISTANCE", label: "Socorro Mecânico" },
    { id: "WINCH", label: "Guincho" },
];

export const optionsOccurrenceTypeEnum = {
    "Socorro Mecânico": "ASSISTANCE",
    "Guincho": "WINCH"
};

export const optionsPayer = [
    { id: "VOKS", label: "Volks|Total" },
    { id: "CHAME_VOLKS", label: "ChameVolks/MAN Service" },
    { id: "UNIFIED_REBOQUE", label: "Prestador Reboque Unificado" },
    { id: "SG", label: "SG (Sistema de Garantia)" },
    { id: "PENDING_ANALYSIS", label: "Pendente Análise Garantia" },
    { id: "OUT_OF_WARRANTY", label: "Fora de Garantia" },
    { id: "VOLKS_CONFIA", label: "VolksConfia" },
    { id: "EXPORTATION", label: "Exportação" },
    { id: "OTHER", label: "Outro" },
];

export const normalizeOccurrenceDispatchType = (type?: string) => {
    switch (type?.trim().toUpperCase()) {
        case "ASSISTANCE":
        case "A":
        case "SOCORRO MECÂNICO":
            return "ASSISTANCE";
        case "WINCH":
        case "W":
        case "GUINCHO":
            return "WINCH";
        default:
            return type;
    }
};

export const isAssistanceOccurrenceDispatch = (type?: string) => {
    return normalizeOccurrenceDispatchType(type) === "ASSISTANCE";
};

export const isCancelledOccurrenceDispatch = (dispatch?: OccurrenceDispatch) => {
    if (!dispatch) {
        return false;
    }

    return dispatch.status === "Indisponível para atendimento" || Boolean(dispatch.reasonRefusal);
};

const getOccurrenceDispatchTime = (dispatch: OccurrenceDispatch) => {
    if (!dispatch.createdAt) {
        return undefined;
    }

    const time = new Date(dispatch.createdAt).getTime();
    return Number.isNaN(time) ? undefined : time;
};

/**
 * Ordena do mais recente para o mais antigo usando createdAt.
 * Sem createdAt é tratado como o mais antigo; empates mantêm a ordem do array (o último é o mais novo).
 */
export const sortOccurrenceDispatchesByMostRecent = (dispatches?: OccurrenceDispatch[]) => {
    return (dispatches ?? [])
        .map((dispatch, index) => ({ dispatch, index, time: getOccurrenceDispatchTime(dispatch) }))
        .sort((first, second) => {
            const firstTime = first.time ?? Number.NEGATIVE_INFINITY;
            const secondTime = second.time ?? Number.NEGATIVE_INFINITY;
            if (firstTime !== secondTime) {
                return secondTime > firstTime ? 1 : -1;
            }
            return second.index - first.index;
        })
        .map(({ dispatch }) => dispatch);
};

export const getLastOccurrenceDispatch = (dispatches?: OccurrenceDispatch[]) => {
    return sortOccurrenceDispatchesByMostRecent(dispatches)[0];
};

export const upsertOccurrenceDispatchInList = (
    dispatches: OccurrenceDispatch[],
    dispatch: OccurrenceDispatch,
) => {
    const dispatchAlreadyExists = dispatches.some((currentDispatch) => currentDispatch.id === dispatch.id);
    if (dispatchAlreadyExists) {
        return dispatches.map((currentDispatch) =>
            currentDispatch.id === dispatch.id ? dispatch : currentDispatch
        );
    }

    return [...dispatches, dispatch];
};

export const getPreviousAssistanceDispatch = (
    dispatches: OccurrenceDispatch[],
    cancelledDispatch: OccurrenceDispatch,
) => {
    const remainingDispatches = dispatches.filter(
        (dispatch) =>
            dispatch.id !== cancelledDispatch.id &&
            isAssistanceOccurrenceDispatch(dispatch.occurrenceType) &&
            !isCancelledOccurrenceDispatch(dispatch) &&
            Boolean(dispatch.dn)
    );

    return getLastOccurrenceDispatch(remainingDispatches);
};

export const applyDealershipToOccurrence = async (
    occurrence: Occurrence,
    dealershipDn: string,
    dispatches: OccurrenceDispatch[],
    setOccurrenceValue: (attr: string, value: any) => void,
) => {
    const dealership = await fetchDealershipsByDN(dealershipDn);
    if (!dealership) {
        return;
    }

    const { data } = await updateOccurrence({
        ...occurrence,
        dispatches,
        dealershipDn: dealership.id,
        dealershipName: dealership.name,
        dealershipRegional: dealership.regional ?? undefined,
        dealershipCell: dealership.cell ?? undefined,
        dealershipUniqueId: dealership.cnpj ?? undefined,
        dealershipLocation: dealership.address ?? undefined,
        dealershipRepresentative: dealership.representative ?? undefined,
    });

    setOccurrenceValue("dealershipDn", dealership.id);
    setOccurrenceValue("dealershipName", dealership.name);
    setOccurrenceValue("dealershipRegional", dealership.regional ?? null);
    setOccurrenceValue("dealershipCell", dealership.cell ?? null);
    setOccurrenceValue("dealershipUniqueId", dealership.cnpj ?? null);
    setOccurrenceValue("dealershipLocation", dealership.address ?? null);
    setOccurrenceValue("dealershipRepresentative", dealership.representative ?? null);
    if (data?.version !== undefined) {
        setOccurrenceValue("version", data.version);
    }
};

export const restorePreviousAssistanceDealership = async (
    occurrence: Occurrence,
    cancelledDispatch: OccurrenceDispatch,
    dispatches: OccurrenceDispatch[],
    setOccurrenceValue: (attr: string, value: any) => void,
) => {
    if (!isAssistanceOccurrenceDispatch(cancelledDispatch.occurrenceType)) {
        return;
    }

    const previousDispatch = getPreviousAssistanceDispatch(dispatches, cancelledDispatch);
    if (!previousDispatch?.dn || previousDispatch.dn === occurrence.dealershipDn) {
        return;
    }

    await applyDealershipToOccurrence(occurrence, previousDispatch.dn, dispatches, setOccurrenceValue);
};

const getDealershipSelectedAddress = (dealership: Dealership) => {
    return [dealership.address, dealership.neighborhood, dealership.city, dealership.state]
        .filter(Boolean)
        .join(", ");
};

const defaultDealershipsMapCenter = { lat: -23.550573750495325, lng: -46.64411772565403 };

export const getDealershipsOrderedForChooseModal = (
    dealerships: Dealership[],
    vehicleGeoLocation?: { lat: number, lng: number },
    occurrenceType?: string,
    occurrenceDealershipDn?: string,
) => {
    const origin = vehicleGeoLocation ?? defaultDealershipsMapCenter;
    const dealershipsWithDistance = dealerships.map((dealership) => ({
        ...dealership,
        distance: getDistanceBetweenTwoLocations(origin, dealership.geoLocation),
    }));

    const dealershipsByProximity = [...dealershipsWithDistance].sort(
        (firstDealership, secondDealership) => (firstDealership.distance ?? 0) - (secondDealership.distance ?? 0)
    );

    if (!isAssistanceOccurrenceDispatch(occurrenceType) || !occurrenceDealershipDn) {
        return dealershipsByProximity;
    }

    const preselectedDealershipIndex = dealershipsByProximity.findIndex(
        (dealership) => dealership.id === occurrenceDealershipDn
    );

    if (preselectedDealershipIndex <= 0) {
        return dealershipsByProximity;
    }

    const preselectedDealership = dealershipsByProximity[preselectedDealershipIndex];
    const nearbyDealershipsWithoutPreselected = dealershipsByProximity.filter(
        (dealership) => dealership.id !== occurrenceDealershipDn
    );

    return [preselectedDealership, ...nearbyDealershipsWithoutPreselected];
};

/**
 * Dipacth card
 */
interface DispatchProps {
    occurrence?: Occurrence | null;
    occurrenceDispatch: OccurrenceDispatch;
    cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
    removeDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<void>;
    status?: string;
    persona?: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
}
export function useDispatch(props: DispatchProps) {
    const { getDealershipByDN } = useDealership();
    const [dealership, setDealership] = useState<Dealership | null>(null);

    const { drivers } = useDrivers();

    const [route, setRoute] = useState<Route | null>(null);
    const [showCancelReasonDialog, setShowCancelReasonDialog] = useState<boolean>(false);

    useEffect(() => {
        if (props.occurrence?.dealershipDn) {
            getDealershipByDN(props.occurrenceDispatch.dn ?? props.occurrence.dealershipDn).then((dealership) => {
                if (dealership) {
                    setDealership(dealership);
                }
            });
        }
    }, [props.occurrence?.dealershipDn, props.occurrenceDispatch.dn]);

    useEffect(() => {
        if (props.occurrenceDispatch.route) {
            getRoute(props.occurrenceDispatch.route).then(({ route, error }) => {
                if (route) {
                    setRoute(route);
                } else {
                    setRoute(null);
                }
            });
        }
    }, [props.occurrenceDispatch.route]);

    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const isDarkMode = useDarkMode();
    const statusColor = (status: string) => {
        switch (status) {
            case "Indisponível para atendimento":
                return isDarkMode ? "#F87171" : "#AB2A34";
            case "Aguardando concessionária":
                return isDarkMode ? "#60A5FA" : "#428AC9";
            default:
                return isDarkMode ? "#34D399" : "#10B981";
        }
    }

    const [newStatus, setNewStatus] = useState<string>(props.status ?? "");
    const [newRefusal, setNewRefusal] = useState<{ reason: string, description: string } | null>({ reason: props.occurrenceDispatch.reasonRefusal ?? "", description: props.occurrenceDispatch.descriptionRefusal ?? "" });
    const [newDriverName, setNewDriverName] = useState<string | null>(props.occurrenceDispatch.driverName ?? null);

    useEffect(() => {
        setNewStatus(props.status ?? "");
    }, [props.status]);

    return {
        optionsOccurrenceType,
        dealership,
        route,
        statusColor,
        newStatus,
        setNewStatus,
        showCancelDialog,
        setShowCancelDialog,
        showCancelReasonDialog,
        setShowCancelReasonDialog,
        newRefusal,
        setNewRefusal,
        drivers,
        newDriverName,
        setNewDriverName,
        optionsPayer,
    }
}

/**
 * Dispatch form
 */
interface DispatchFormProps {
    occurrence?: Occurrence | null;
    upsertDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
    dispatches: OccurrenceDispatch[];
    updateDispatchList: (dispatch: OccurrenceDispatch) => void;
    persona?: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
    setOccurrenceValue?: (attr: string, value: any) => void;
}

export function useDispatchForm(props: DispatchFormProps) {
    const intl = useIntl();
    const optionsReleasePayment = [
        { id: "true", label: "Sim" },
        { id: "false", label: "Não" },
    ];

    const [loading, setLoading] = useState(false);
    const [upsertDispatchLoading, setAddDispatchLoading] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorDialogBody, setErrorDialogBody] = useState<string>("");
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const [showChooseDealershipDialog, setShowChooseDealershipDialog] = useState(false);

    const [dispatchForm, setDispatchForm] = useState<{
        id?: string;
        occurrenceType?: string;
        payer?: string;
        releasePayment?: string;
    }>({
        id: undefined,
        occurrenceType: undefined,
        payer: undefined,
        releasePayment: undefined,
    });

    const handleAddDispatch = async (occurrenceDn: string): Promise<boolean> => {
        if (!props.occurrence || !props.upsertDispatch) {
            setShowErrorDialog(true);
            setErrorDialogBody(intl.formatMessage({ id: 'intl-msg:dispatch.error.occurrence-not-found' }));
            return false;
        }
        
        setAddDispatchLoading(true);
        const { error, dispatch } = await props.upsertDispatch(props.occurrence, {
            occurrenceType: mapOccurrenceDispatchType(dispatchForm.occurrenceType),
            payer: dispatchForm.payer,
            authorizePayment: dispatchForm.releasePayment === "true",
            dn: occurrenceDn,
            route: '-',
        });

        if (error) {
            setShowErrorDialog(true);
            setErrorDialogBody(error ?? intl.formatMessage({ id: 'intl-msg:dispatch.error.body' }));
            setAddDispatchLoading(false);
            return false;
        }

        if (dispatch) {
            props.updateDispatchList(dispatch);
        }

        setDispatchForm({
            id: dispatch?.id,
            occurrenceType: dispatch?.occurrenceType ?? dispatchForm.occurrenceType,
            payer: dispatch?.payer,
            releasePayment: dispatch?.authorizePayment ? "true" : "false",
        });
        setAddDispatchLoading(false);
        return true;
    }
    
    const handleUpdateDispatch = async (dn: string, route: string, originLocation: string, destinationLocation: string) => {
        if (!props.occurrence || !props.upsertDispatch) {
            setShowErrorDialog(true);
            setErrorDialogBody(intl.formatMessage({ id: 'intl-msg:dispatch.error.occurrence-not-found' }));
            return;
        }

        setLoading(true);
        const { error, dispatch } = await props.upsertDispatch(props.occurrence, {
            id: dispatchForm.id,
            occurrenceType: mapOccurrenceDispatchType(dispatchForm.occurrenceType),
            payer: dispatchForm.payer,
            authorizePayment: dispatchForm.releasePayment === "true",
            dn,
            route,
            originLocation: originLocation,
            destinationLocation: destinationLocation,
        });
        if (dispatch) {
            setShowSuccessDialog(true);
            props.updateDispatchList(dispatch);
            await syncOccurrenceDealershipFromAssistanceDispatch(dn, dispatch);
        } else {
            setShowErrorDialog(true);
            setErrorDialogBody(error ?? intl.formatMessage({ id: 'intl-msg:dispatch.error.body' }));
        }
        setLoading(false);
    }

    const syncOccurrenceDealershipFromAssistanceDispatch = async (
        dealershipDn: string,
        updatedDispatch: OccurrenceDispatch,
    ) => {
        if (!props.occurrence || !props.setOccurrenceValue || !isAssistanceOccurrenceDispatch(dispatchForm.occurrenceType)) {
            return;
        }

        if (dealershipDn === props.occurrence.dealershipDn) {
            return;
        }

        await applyDealershipToOccurrence(
            props.occurrence,
            dealershipDn,
            upsertOccurrenceDispatchInList(props.occurrence.dispatches ?? [], updatedDispatch),
            props.setOccurrenceValue,
        );
    }

    const mapOccurrenceDispatchType = (occurrenceType?: string) => {
        switch (occurrenceType) {
            case "ASSISTANCE":
                return "ASSISTANCE";
            case "Socorro Mecânico":
                return "ASSISTANCE";
            case "Guincho":
                return "WINCH";
            case "WINCH":
                return "WINCH";
            default:
                return occurrenceType;
        }
    }

    return {
        optionsOccurrenceType,
        optionsPayer,
        optionsReleasePayment,
        loading,
        upsertDispatchLoading,
        showErrorDialog,
        errorDialogBody,
        showSuccessDialog,
        setShowErrorDialog,
        setShowSuccessDialog,
        dispatchForm,
        setDispatchForm,
        handleAddDispatch,
        handleUpdateDispatch,
        showChooseDealershipDialog,
        setShowChooseDealershipDialog,
    };
}

/**
 * Dispatch choose dealership
 */
interface AddressForm {
    address: string;
    neighborhood: string;
    city: string;
    state: string;
    label?: string;
    geoLocation?: AssetGeoLocation;
    
}

interface Props { 
    show: boolean, 
    responsibleId: string, 
    assetId: string, 
    occurrenceType?: string, 
    chassis?: string,
    ocurrenceId?: string, 
    dispatchId?: string,
    vehicleName?: string,
    occurrenceDealershipDn?: string,
}

export function useDispatchChooseDealership(props: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [addressForm, setAddressForm] = useState<AddressForm>({ address: "", neighborhood: "", city: "", state: "", label: "", geoLocation: undefined });
    const [addressGeoLocation, setAddressGeoLocation] = useState<{ lat: number, lng: number }>({ lat: -23.550573750495325, lng: -46.64411772565403 });
    const [showSearchDealership, setShowSearchDealership] = useState<boolean>(false);
    const [searchDealership, setSearchDealership] = useState<boolean>(false);
    const [searchDealershipTerm, setSearchDealershipTerm] = useState("");
    const [dealerships, setDealerships] = useState<Dealership[]>([]);

    const [dealershipSelected, setDealershipSelected] = useState<{
        dn: string;
        address: string;
        geoLocation?: AssetGeoLocation;
    } | null>(null);
    const [showSearchConcessionaria, setShowSearchConcessionaria] = useState<boolean>(false);

    const [createRouteError, setCreateRouteError] = useState<string | null>(null);

    const [enableLocation, setEnableLocation] = useState(true);
    const [assetGeoLocation, setAssetGeoLocation] = useState<AssetGeoLocation>();
    const [initialAssetAddress, setInitialAssetAddress] = useState<{ address: string, neighborhood: string, state: string, city: string, geoLocation: AssetGeoLocation }>();

    const defaultLatLong = { lat: -23.550573750495325, lng: -46.64411772565403 }

    const searchDealerships = async (geoLocation: { lat: number, lng: number }) => {
        setLoading(true);
        setAddressGeoLocation(geoLocation);
        const dealerships = await fetchDealershipsByGeolocation(geoLocation.lat, geoLocation.lng);
        setLoading(false);
        setDealerships(dealerships);
        setSearchDealership(true);
    }

    const getDealershipsLocation = async () => {
        const lat = initialAssetAddress?.geoLocation.lat || defaultLatLong.lat;
        const lng = initialAssetAddress?.geoLocation.lng || defaultLatLong.lng;
        const dealerships = await fetchDealershipsByGeolocation(lat, lng);

        handleSetEnableLocation(true);
        setDealerships(dealerships)
    }

    const applyAssistanceOccurrenceDealershipDefault = async (
        nearbyDealerships: Dealership[],
        cancelled: boolean,
    ) => {
        if (!isAssistanceOccurrenceDispatch(props.occurrenceType) || !props.occurrenceDealershipDn) {
            setDealerships(nearbyDealerships);
            return;
        }

        let occurrenceDealership = nearbyDealerships.find(
            (dealership) => dealership.id === props.occurrenceDealershipDn
        );

        if (!occurrenceDealership) {
            occurrenceDealership = await fetchDealershipsByDN(props.occurrenceDealershipDn) ?? undefined;
            if (cancelled) {
                return;
            }
        }

        if (!occurrenceDealership) {
            setDealerships(nearbyDealerships);
            return;
        }

        const otherNearbyDealerships = nearbyDealerships.filter(
            (dealership) => dealership.id !== occurrenceDealership.id
        );

        setDealerships([occurrenceDealership, ...otherNearbyDealerships]);
        setDealershipSelected({
            dn: occurrenceDealership.id,
            address: getDealershipSelectedAddress(occurrenceDealership),
        });
    };

    useEffect(() => {
        if (!props.show) {
            setDealerships([]);
            setDealershipSelected(null);
            setSearchDealershipTerm("");
            setAddressForm({ address: "", neighborhood: "", city: "", state: "", label: "", geoLocation: undefined });
            return;
        }

        if (!props.assetId) return;

        let cancelled = false;

        const loadDealershipsOnOpen = async () => {
            setDealerships([]);
            setDealershipSelected(null);
            setSearchDealershipTerm("");
            setCreateRouteError(null);

            const vehicleGeoLocation = await fetchAssetGeoLocation(props.assetId);
            if (cancelled) return;

            if (vehicleGeoLocation?.lat && vehicleGeoLocation?.lng) {
                setAssetGeoLocation(vehicleGeoLocation);

                const assetAddress = await getAddressByGeoLocation(
                    vehicleGeoLocation.lat,
                    vehicleGeoLocation.lng
                );
                if (cancelled) return;

                setInitialAssetAddress({
                    address: assetAddress.address ?? "",
                    neighborhood: assetAddress.neighborhood ?? "",
                    state: assetAddress.state ?? "",
                    city: assetAddress.city ?? "",
                    geoLocation: vehicleGeoLocation,
                });

                setAddressForm({
                    address: assetAddress.address ?? "",
                    neighborhood: assetAddress.neighborhood ?? "",
                    city: assetAddress.city ?? "",
                    state: assetAddress.state ?? "",
                    label: "",
                    geoLocation: vehicleGeoLocation,
                });

                const nearbyDealerships = await fetchDealershipsByGeolocation(
                    vehicleGeoLocation.lat,
                    vehicleGeoLocation.lng
                );
                if (cancelled) return;
                await applyAssistanceOccurrenceDealershipDefault(nearbyDealerships, cancelled);
            } else {
                setEnableLocation(true);
                setAssetGeoLocation(defaultLatLong);
                const nearbyDealerships = await fetchDealershipsByGeolocation(
                    defaultLatLong.lat,
                    defaultLatLong.lng
                );
                if (cancelled) return;
                await applyAssistanceOccurrenceDealershipDefault(nearbyDealerships, cancelled);
            }
        };

        loadDealershipsOnOpen();

        return () => {
            cancelled = true;
        };
    }, [props.show, props.assetId, props.occurrenceDealershipDn, props.occurrenceType]);

    const handleSetEnableLocation = (manualLocationEnabled: boolean) => {
        setEnableLocation(manualLocationEnabled);


        if (manualLocationEnabled) [setAssetGeoLocation(defaultLatLong), setAddressForm({ address: '', neighborhood: '', state: '', city: '' })]
        else [
            setAssetGeoLocation(initialAssetAddress?.geoLocation), 
            setAddressForm({ address: initialAssetAddress?.address ?? '', neighborhood: initialAssetAddress?.neighborhood ?? '', state: initialAssetAddress?.state ?? '', city: initialAssetAddress?.city ?? '' })
        ];
        setShowSearchConcessionaria(false);
    };
   
    const createRoutingRoute = async (): Promise<{ routeId: string, origin?: string, destination?: string }> => {
        if (!dealershipSelected) {
            setCreateRouteError("Concessionária não selecionada");
            return { routeId: "", origin: "", destination: "" };
        }

        const dealershipAddress = dealershipSelected.address;
        const vehicleAddress = addressForm.label ?? `${[addressForm.address, addressForm.neighborhood, addressForm.city, addressForm.state].filter(Boolean).join(", ")}`;
        const vehicleGeoLocation = addressForm.geoLocation ?? assetGeoLocation;

        // WINCH: origem = veículo, destino = concessionária. Demais tipos: origem = concessionária, destino = veículo.
        const origin = props.occurrenceType === 'WINCH'
            ? { address: vehicleAddress, lat: vehicleGeoLocation?.lat, lng: vehicleGeoLocation?.lng }
            : { address: dealershipAddress, lat: dealershipSelected.geoLocation?.lat, lng: dealershipSelected.geoLocation?.lng };
        const destination = props.occurrenceType === 'WINCH'
            ? { address: dealershipAddress, lat: dealershipSelected.geoLocation?.lat, lng: dealershipSelected.geoLocation?.lng }
            : { address: vehicleAddress, lat: vehicleGeoLocation?.lat, lng: vehicleGeoLocation?.lng };

        // WINCH usa TRUCK; demais tipos usam CAR.
        const vehicleType = props.occurrenceType === 'WINCH' ? "TRUCK" : "CAR";
        // WINCH permanece 01:00 no local da pane; demais tipos não têm permanência (00:00).
        const stayTime = props.occurrenceType === 'WINCH' ? "01:00" : "00:00";
        
        const { wasCreated, routeId, error } = await createv2Route(
                origin, 
                destination, 
                `Acionamento ${new Date().toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 
                vehicleType,
                dealershipSelected.dn,
                props.chassis,
                props.dispatchId,
                props.vehicleName,
                stayTime
            );
        if (wasCreated) {
            setCreateRouteError(null);
            setDealerships([]);
            return { routeId, origin: origin.address, destination: destination.address };
        } else {
            setCreateRouteError(error ?? "Erro ao criar rota");
            return { routeId: "", origin: "", destination: "" };
        }
    }

    const handleSearchOnAddress = async () => {
        if (!addressForm?.city) {
            return;
        };

        if (addressForm.geoLocation?.lat) {
            const dealerships = await fetchDealershipsByGeolocation(addressForm.geoLocation?.lat, addressForm.geoLocation?.lng);
            setDealerships(dealerships)
        } else {
            const dealerships = await fetchDealershipsByStateAndCity(addressForm.state, addressForm.city);
            setDealerships(dealerships)
        }
        
    }

    useEffect(() => {
        const fetchData = async () => {
            const dealerships = await fetchDealerships(searchDealershipTerm, searchDealershipTerm);
            setDealerships(dealerships);
        }

        if(searchDealershipTerm?.length > 2) fetchData();
    }, [searchDealershipTerm]);

    useEffect(() => {
        handleSearchOnAddress()
    }, [addressForm?.city])

    return {
        loading,
        saveLoading,
        setSaveLoading,
        addressForm,
        setAddressForm,
        addressGeoLocation,
        searchDealership,
        searchDealershipTerm,
        setSearchDealershipTerm,
        dealerships: getDealershipsOrderedForChooseModal(
            dealerships,
            assetGeoLocation,
            props.occurrenceType,
            props.occurrenceDealershipDn,
        ),
        setDealershipSelected,
        dealershipSelected,
        searchDealerships,
        createRouteError,
        setCreateRouteError,
        createRoutingRoute,
        showSearchDealership,
        showSearchConcessionaria, setShowSearchConcessionaria,
        enableLocation, handleSetEnableLocation,
        assetGeoLocation,
        setAssetGeoLocation,
        getDealershipsLocation,
    }
}